import React from "react";
import InvoiceIndication from "./list/InvoiceIndication";
import DocumentIndication from "./list/DocumentIndication";
import ShowDateForList from "show/components/ShowDateForList";
import ShowTime from "show/components/ShowTime";
import ShowNameForListInternal from "show/components/ShowNameForListInternal";
import VisibleToExternalIcon from "components/VisibleToExternal/VisibleToExternalIcon";
import PublicationToWebIcon from "components/PublicationToWeb/PublicationToWebIcon";
import TagList from "components/Tag/TagList";
import { type ViewportType } from "utilities/hookutils";
import CostSalesForList from "show/components/CostSalesForList";
import { type TableColumn } from "components/Tables";

interface DocumentIndicationProps {
  documentIndications: server.dto.DocumentIndication[];
  showId: number;
}

export const DocumentIndications = ({ documentIndications, showId }: DocumentIndicationProps) => {
  return (
    <div className="flex flex-wrap">
      {documentIndications.map((indication, idx) =>
        indication.documentType === "invoice"
          ? <InvoiceIndication
            key={`${idx}_${indication.color}_${indication.count}_${indication.documentCode}`}
            documentIndication={indication}
            showId={showId}
          />
          : <DocumentIndication
            key={`${idx}_${indication.color}_${indication.count}_${indication.documentCode}`}
            documentIndication={indication}
            showId={showId}
          />)}
    </div>
  );
};

export const tableLayout = (viewport: ViewportType, _: server.viewmodel.ShowListInternalFrontModel): Array<TableColumn<server.dto.ShowForListInternal>> => [{
  key: "date",
  header: RESX.DateTime.lblDate,
  render: (show: server.dto.ShowForListInternal) => <div className="flex-column">
    <ShowDateForList dateTime={show.dateTime} />
    <ShowTime dateTime={show.dateTime} />
  </div>,
  size: "120px"
}, {
  key: "name",
  header: RESX.GeneralLabels.Name,
  render: (show: server.dto.ShowForListInternal) => <ShowNameForListInternal show={show} />,
  size: "1fr"
}, {
  key: "artist",
  header: site.currentEnvironment.functionGroups.artist.singular,
  render: (show: server.dto.ShowForListInternal) => <div title={show.artist?.name} className="text-ellipsis">{show.artist?.name}</div>,
  size: "minmax(80px, 1fr)",
  hidden: !site.currentEnvironment.functionGroups.artist.enabled
}, {
  key: "venue",
  header: site.currentEnvironment.functionGroups.venue.singular,
  render: (show: server.dto.ShowForListInternal) => <>
    <div title={show.venue?.name} className="text-ellipsis">{show.venue?.name}</div>
    {show.venue?.cityCountry && <div title={show.venue?.cityCountry} className="text-ellipsis text-sublabel">
      {show.venue.cityCountry}
    </div>}
  </>,
  size: "minmax(80px, 1fr)",
  hidden: !site.currentEnvironment.functionGroups.venue.enabled
}, {
  key: "promoter",
  header: site.currentEnvironment.functionGroups.promoter.singular,
  render: (show: server.dto.ShowForListInternal) => <div title={show.promoterName} className="text-ellipsis">{show.promoterName}</div>,
  hidden: !site.currentEnvironment.functionGroups.promoter.enabled,
  size: "minmax(80px, 1fr)"
}, {
  key: "hotel",
  header: site.currentEnvironment.functionGroups.hotel.singular,
  render: (show: server.dto.ShowForListInternal) => <div title={show.hotelName} className="text-ellipsis">{show.hotelName}</div>,
  hidden: (!site.currentEnvironment.functionGroups.hotel.enabled || !["desktopUltraWide", "desktopWide"].includes(viewport)),
  size: "minmax(80px, 1fr)"
}, {
  key: "sales",
  header: RESX.Financial.Sales,
  render: (show: server.dto.ShowForListInternal) => <CostSalesForList value={show.salesExcludingTax} baseCurrencyFormat="visible" />,
  hidden: (!site.permissions.show.canReadSales || !["desktopUltraWide", "desktopWide", "desktopMedium"].includes(viewport)),
  className: "s1_cell_financialamount",
  size: "auto"
},
{
  key: "icons",
  header: "",
  render: (show: server.dto.ShowForListInternal) => <div className="flex-column align-center">
    <VisibleToExternalIcon visibleToExternal={show.visibleToExternal} />
    <PublicationToWebIcon publicationToWeb={show.publicationToWeb} />
  </div>,
  size: "30px"
}, {
  key: "documentIndications",
  header: "",
  render: (show: server.dto.ShowForListInternal) => <DocumentIndications documentIndications={show.documentIndications} showId={show.showId} />,
  size: "fit-content(60px)"
}, {
  key: "tags",
  header: "",
  render: item => <TagList tags={item.tags} length="short" />,
  size: "fit-content(200px)"
}];
